import {
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type WithFieldValue,
} from 'firebase/firestore';
import type { ZodType } from 'zod';

import { AppError } from '@/infrastructure/errors/AppError';

/**
 * Recursively replaces Firestore `Timestamp` values with plain `Date`.
 * The domain layer knows nothing about Firebase, so the translation has to
 * happen here at the boundary.
 */
function toDomainValue(value: unknown): unknown {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  if (value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(toDomainValue);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        toDomainValue(entry),
      ]),
    );
  }
  return value;
}

/**
 * Builds a converter that validates every document against its schema on read.
 *
 * A malformed document fails here, loudly, naming the collection and id —
 * instead of travelling on as `undefined` and surfacing as an empty cell in a
 * table halfway through the tournament.
 */
export function createConverter<T>(
  schema: ZodType<T>,
  label: string,
  /**
   * How the document id enters the parsed object. Most collections store it as
   * `id`; `users` uses `uid`. Passing it in keeps the id authoritative from the
   * document path rather than from a field that could drift.
   */
  identity: (documentId: string) => Record<string, string> = (documentId) => ({ id: documentId }),
): FirestoreDataConverter<T> {
  return {
    toFirestore(model: WithFieldValue<T>): DocumentData {
      return model as DocumentData;
    },

    fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): T {
      const raw = toDomainValue(snapshot.data(options)) as Record<string, unknown>;
      const result = schema.safeParse({ ...raw, ...identity(snapshot.id) });

      if (!result.success) {
        const details = result.error.issues
          .map((issue) => `${issue.path.join('.') || '(Wurzel)'}: ${issue.message}`)
          .join('; ');

        throw new AppError(
          'data-integrity',
          `Dokument ${label}/${snapshot.id} entspricht nicht dem erwarteten Aufbau — ${details}`,
          result.error,
        );
      }

      return result.data;
    },
  };
}
